import { Link } from 'react-scroll';
import { Icon } from '@iconify/react';
import { useState, useEffect, ReactNode } from 'react';
import menu2Fill from '@iconify/icons-eva/menu-2-fill';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
// material
import { styled } from '@mui/material/styles';
import {
  Box,
  List,
  Link as LinkMaterial,
  Drawer,
  Collapse,
  LinkProps,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  ListItemButtonProps,
  Grid,
  Button
} from '@mui/material';
// components
import Logo from '../../components/Logo';
import NavSection from '../../components/NavSection';
import Scrollbar from '../../components/Scrollbar';
import { MIconButton } from '../../components/@material-extend';
//
import { MenuProps, MenuItemProps } from './MainNavbar';
import useTranslations from 'hooks/useTranslations';
import useWordings from 'locales/wordings/useWordings';

// ----------------------------------------------------------------------

const ICON_SIZE = 22;
const ITEM_SIZE = 48;
const PADDING = 2.5;

type StyleProps = LinkProps & ListItemButtonProps;

interface ListItemStyleProps extends StyleProps {
  component?: ReactNode;
  to?: string;
  end?: boolean;
}

const ListItemStyle = styled(ListItemButton)<ListItemStyleProps>(({ theme }) => ({
  ...theme.typography.body2,
  height: ITEM_SIZE,
  textTransform: 'capitalize',
  paddingLeft: theme.spacing(PADDING),
  paddingRight: theme.spacing(2.5),
  color: theme.palette.text.secondary
}));

// ----------------------------------------------------------------------

type MenuMobileItemProps = {
  item: MenuItemProps;
  isOpen: boolean;
  onOpen: VoidFunction;
};

function MenuMobileItem({ item, isOpen, onOpen }: MenuMobileItemProps) {
  const { title, path, icon, children } = item;
  const { hash } = useLocation();

  if (children) {
    return (
      <>
        <Link
          key={title}
          activeClass="active"
          to={'#' + path}
          spy={true}
          smooth={true}
          offset={-70}
          duration={500}
        >
          <ListItemStyle onClick={onOpen} sx={{ color: 'white' }}>
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText primary={title} />
            <Box
              component={Icon}
              icon={isOpen ? arrowIosDownwardFill : arrowIosForwardFill}
              sx={{ width: 16, height: 16, ml: 1 }}
            />
          </ListItemStyle>
        </Link>

        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column-reverse' }}>
            <NavSection
              navConfig={children}
              sx={{
                '& .MuiListSubheader-root': {
                  pl: PADDING,
                  display: 'flex',
                  alignItems: 'center',
                  '&:before': {
                    ml: '6px',
                    mr: '22px',
                    width: 8,
                    height: 2,
                    content: "''",
                    borderRadius: 2
                  }
                },
                '& .MuiListItemButton-root': {
                  pl: PADDING,
                  '&:before': { display: 'none' },
                  '&.active': { color: 'primary.main', bgcolor: 'transparent' }
                },
                '& .MuiListItemIcon-root': {
                  width: ICON_SIZE,
                  height: ICON_SIZE,
                  '&:before': {
                    width: 4,
                    height: 4,
                    content: "''",
                    borderRadius: '50%'
                  }
                }
              }}
            />
          </Box>
        </Collapse>
      </>
    );
  }

  return (
    <Link
      key={title}
      activeClass="active"
      to={path}
      spy={true}
      smooth={true}
      offset={-70}
      duration={500}
    >
      <ListItemStyle
        to={'#' + path}
        component={RouterLink}
        end={path === '/'}
        sx={{
          color: 'white',
          '&.active': {
            fontWeight: 'fontWeightMedium',
            ...(hash === `#${path}` && { color: 'primary.main' })
          }
        }}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText disableTypography primary={title} />
      </ListItemStyle>
    </Link>
  );
}

export default function MenuMobile({ isOffset, isHome, navConfig }: MenuProps) {
  const { currentLang } = useTranslations();
  const { menuWordings } = useWordings();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (drawerOpen) {
      handleDrawerClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleOpen = () => {
    setOpen(!open);
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <>
      <MIconButton
        onClick={handleDrawerOpen}
        sx={{
          ml: 1,
          ...(isHome && { color: 'common.white' }),
          ...(isOffset && { color: 'text.primary' })
        }}
      >
        <Icon icon={menu2Fill} />
      </MIconButton>

      <Drawer
        open={drawerOpen}
        anchor="right"
        onClose={handleDrawerClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { pb: 5, width: 260 } }}
      >
        <Scrollbar>
          <Grid container justifyContent="center" alignItems="center">
            <Link
              key={navConfig[0].title}
              activeClass="active"
              to={navConfig[0].path}
              spy={true}
              smooth={true}
              offset={-70}
              duration={500}
            >
              <LinkMaterial
                component={RouterLink}
                to={'#' + navConfig[0].path}
                sx={{ display: 'inline-flex' }}
              >
                <Logo sx={{ mx: PADDING, my: 3, width: 140, height: 140 }} />
              </LinkMaterial>
            </Link>
          </Grid>

          <List disablePadding>
            {navConfig.map((link) => (
              <MenuMobileItem key={link.title} item={link} isOpen={open} onOpen={handleOpen} />
            ))}
          </List>

          <Grid container justifyContent="center" sx={{ p: 2 }}>
            <Button
              variant="contained"
              fullWidth
              href={'/static/TheVoiceCrypto-PitchDeck.pdf'}
              target="_blank"
              rel="noreferrer"
              sx={{ color: 'white' }}
            >
              {menuWordings.pitchDeck}
            </Button>
          </Grid>

          <Grid container justifyContent="center" sx={{ px: 2 }}>
            <Button
              variant="contained"
              fullWidth
              href={
                currentLang.value === 'es'
                  ? 'https://whitepaper.thevoicecrypto.org/the-voice-crypto/introduccion'
                  : 'https://whitepaper.thevoicecrypto.org/v/english/the-voice-crypto/introduction'
              }
              target="_blank"
              rel="noreferrer"
              sx={{ color: 'white' }}
            >
              {menuWordings.whitepaper}
            </Button>
          </Grid>
        </Scrollbar>
      </Drawer>
    </>
  );
}
